import { useEffect, useState } from 'react';
import {
  Box,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import CustomModal from 'src/components/modal/customModal';
import { styled, useTheme } from '@mui/material/styles';
import FormProvider, { RHFCheckbox, RHFMultiCheckbox, RHFRadioGroup, RHFSelect, RHFTextField } from 'src/components/hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { axiosGetCall,  axiosPostCall } from 'src/services/apisCall';
import { SMS, allowedCountries } from 'src/lib/Enums';
import { checkButtonAccess } from 'src/utils/common';
import { jwtDecode } from 'jwt-decode';

const InputContainer = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  gap: '20px',
}));
const FieldHeading = styled(Typography)(() => ({
  fontWeight: 500,
}));

const GatewaysModal = ({ openModal, handleCloseModal, title, forceLoad, updateUserId}) => {
  const [canSubmit, setCanSubmit] = useState(true);
  const [gatewayDetails, setGatewayDetails] = useState({});

  const { email } = jwtDecode(localStorage.getItem('token'));

  const { GATEWAY_ROUTE, GATEWAY_TYPES, SMSC_TYPE } = SMS;
  const theme = useTheme();

  const Schema = Yup.object().shape({
    name: Yup.string().required('Name is required!'),
    country: Yup.string().required('Country is required!'),
    route: Yup.string().required('Route is required!'),
    gatewayType: Yup.string().required('Gateway type is required!'),
    // senderId: Yup.string().required('Sender ID is required!'),
      senderId: Yup.string()
    .required('Sender ID is required!')
    .when('route', {
      is: 'promotional',
      then: (schema) =>
        schema.matches(/^\d{6}$/, 'Sender ID must be a 6-digit number for promotional route'),
      otherwise: (schema) =>
        schema.matches(/^[A-Za-z]{6}$/, 'Sender ID must be 6 alphabets for non-promotional routes'),
    }),
    minimumCredit: Yup.string().required('Minimum credit is required!'),
    smscHost: Yup.string().required('SMSC host is required!'),
    coding_english: Yup.string().required('Coding (English) is required!'),
    coding_other: Yup.string().required('Coding (Other) is required!'),
    charset_english: Yup.string().required('Charset (English) is required!'),
    charset_other: Yup.string().required('Charset (Other) is required!'),
    service_english: Yup.string().required('Service (English) is required!'),
    service_other: Yup.string().required('Service (Other) is required!'),
    sms_type_english: Yup.string().required('SMS Type (English) is required!'),
    sms_type_other: Yup.string().required('SMS Type (Other) is required!'),
    mclass_english: Yup.string().required('MClass (English) is required!'),
    mclass_other: Yup.string().required('MClass (Other) is required!'),
    alt_dcs_english: Yup.string().required('Alt DCS (English) is required!'),
    alt_dcs_other: Yup.string().required('Alt DCS (Other) is required!'),
    dedicated: Yup.string().required('Dedicated is required!'),
    smsc_port: Yup.string().required('SMSC TX (Sending) port is required!'),
    smsc_receive_port: Yup.string().required('SMSC RX (Receive) port is required!'),
    smscUsername: Yup.string().required('SMSC Username is required!'),
    smscPassword: Yup.string().required('SMSC Password is required!'),
    smscSystemType: Yup.string().required('SMSC System Type is required!'),
    throughput: Yup.string().required('Throughput is required!'),
    noSessions: Yup.string().required('No. of TX (Sending) Sessions per second (TPS) is required!'),
    smscType: Yup.string().required('SMSC Connection Type is required!'),
    noReceiveSessions: Yup.string().required('No. of RX (Receive) Sessions is required!'),
    perSessionsTPS: Yup.number() .required('Per sessions sending (TPS) is required!'),
  });

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues: {
      name: '',
      dvl_report: '',
      country: '',
      route: '',
      gatewayType: '',
      senderId: '',
      minimumCredit: '',   
      smscHost: '',
      smscType: '',
      smsc_receive_port: '',
      smscUsername: '',
      smscPassword: '',
      smscSystemType: 'SMPP',
      throughput: '',
      noSessions: '',
      noReceiveSessions: '',
      perSessionsTPS: '',
      coding_english: '0',
      coding_other: '2',
      charset_english: 'Null',
      charset_other: 'ucs-2',    
      service_english: 'sm',
      service_other: 'Null',    
      sms_type_english: '2',
      sms_type_other: 'Null',    
      mclass_english: '1',
      mclass_other: 'Null',    
      alt_dcs_english: '1',
      alt_dcs_other: '1',
      dedicated: 0,
      smsc_port: '',    
    },
  });

  const { control, watch, setValue, handleSubmit, reset, formState: { errors } } = methods;

  // Count total validation errors
  const totalErrors = Object.keys(errors).length;

  const SMSC_DELIVERY_REPORT = [
    { value: 1, label: "Delivery Success" },
    { value: 2, label: "Delivery Failure" },
    { value: 4, label: "Message Buffered" },
    { value: 8, label: "SMSC Submit" },
    { value: 16, label: "SMSC Reject" }
  ];

 const selectedCheckboxes = watch('dvl_report') || [];

  const allSelected = selectedCheckboxes.length === SMSC_DELIVERY_REPORT.length;

  const handleAllToggle = () => {
    setValue('dvl_report', allSelected ? [] : SMSC_DELIVERY_REPORT.map((opt) => opt.value));
  };

  const handleIndividualChange = (value) => {
    const newSelectedValues = selectedCheckboxes.includes(value)
      ? selectedCheckboxes.filter((item) => item !== value) // Remove value if already selected
      : [...selectedCheckboxes, value]; // Add value if not selected

    setValue('dvl_report', newSelectedValues);

    setValue('all', newSelectedValues.length === SMSC_DELIVERY_REPORT.length);
  };
  const onSubmit = async(data) => {
    setCanSubmit(false);
  try{
    const payload = {
      name: data.name,
      countryCode: data.country,
      route: data.route,
      gateway_type: data.gatewayType,
      sender_id: data.senderId,
      minimum_credit: data.minimumCredit,
      smsc_host: data.smscHost,
      smsc_connection_type: data.smscType,
      smsc_port: data.smsc_port,
      smsc_receive_port: data.smsc_receive_port,
      smsc_username: data.smscUsername,
      smsc_password: data.smscPassword,
      smsc_system_type: data.smscSystemType,
      throughput: data.throughput,
      no_of_send_session: data.noSessions,
      no_of_receive_session: data.noReceiveSessions,
      per_send_session_tps: data.perSessionsTPS,
      coding: data.coding_english,
      coding_other: data.coding_other,
      charset: data.charset_english,
      charset_other: data.charset_other,
      service: data.service_english,
      service_other: data.service_other,
      sms_type: data.sms_type_english,
      sms_type_other: data.sms_type_other,
      mclass: data.mclass_english,
      mclass_other: data.mclass_other,
      alt_dcs: data.alt_dcs_english,
      alt_dcs_other: data.alt_dcs_other,
      dvl_report_codes: data.dvl_report,
      is_dedicated: data.dedicated,
    };
    if (updateUserId) {
      payload.gateway_id = updateUserId,
      payload.adminEmail = email;
      await axiosPostCall('updateGateway', payload);
    } else{
      await axiosPostCall('addGateway', payload);
    }

    renderSuccessMessage(updateUserId ? 'Gateway updated successfully' : 'Gateway added successfully');
    handleCloseModal();
    reset();
    forceLoad(true);

  } catch (error) {
    // logger.error('Add Gateway [handleSubmit]', error);
    const errorMessage = error?.message;
    renderErrorMessage(errorMessage);
  } finally {
    setCanSubmit(true);
  }
  };
  const onCancel = () => {
    reset ();
  };

useEffect(() => {
  if (updateUserId) {
    const fetchGatewayList = async () => {
      try {
        const { gatewayList } = await axiosGetCall("getListGateway", { gatewayId: updateUserId });
        setGatewayDetails(gatewayList[0]);
      } catch (error) {
        // logger.error("Gateway List", error);
        renderErrorMessage(error?.message);
      }
    };

    fetchGatewayList();
  } else {
     setGatewayDetails({});
  }
}, [updateUserId]);



useEffect(() => {
  if (gatewayDetails) {
    setValue('name', gatewayDetails?.name);
    setValue('country', gatewayDetails?.countryCode);
    setValue('route', gatewayDetails?.route);
    setValue('gatewayType', gatewayDetails?.gateway_type);
    setValue('senderId', gatewayDetails?.sender_id);
    setValue('minimumCredit', gatewayDetails?.minimum_credit);
    setValue('smscHost', gatewayDetails?.gateway_details?.smsc_host);
    setValue('smscType', gatewayDetails?.gateway_details?.smsc_connection_type);
    setValue('smsc_port', gatewayDetails?.gateway_details?.smsc_port);
    setValue('smsc_receive_port', gatewayDetails?.gateway_details?.smsc_receive_port);
    setValue('smscUsername', gatewayDetails?.gateway_details?.smsc_username);
    setValue('smscPassword', gatewayDetails?.gateway_details?.smsc_password);
    setValue('smscSystemType', gatewayDetails?.gateway_details?.smsc_system_type);
    setValue('throughput', gatewayDetails?.gateway_details?.throughput);
    setValue('noSessions', gatewayDetails?.no_of_send_session);
    setValue('noReceiveSessions', gatewayDetails?.no_of_receive_session);
    setValue('perSessionsTPS', gatewayDetails?.per_send_session_tps);
    setValue('coding_english', gatewayDetails?.gateway_details?.coding);
    setValue('coding_other', gatewayDetails?.gateway_details?.coding_other);
    setValue('charset_english', gatewayDetails?.gateway_details?.charset);
    setValue('charset_other', gatewayDetails?.gateway_details?.charset_other);
    setValue('service_english', gatewayDetails?.gateway_details?.service);
    setValue('service_other', gatewayDetails?.gateway_details?.service_other);
    setValue('sms_type_english', gatewayDetails?.gateway_details?.sms_type);
    setValue('sms_type_other', gatewayDetails?.gateway_details?.sms_type_other);
    setValue('mclass_english', gatewayDetails?.gateway_details?.mclass);
    setValue('mclass_other', gatewayDetails?.gateway_details?.mclass_other);
    setValue('alt_dcs_english', gatewayDetails?.gateway_details?.alt_dcs);
    setValue('alt_dcs_other', gatewayDetails?.gateway_details?.alt_dcs_other);
    setValue('dvl_report', gatewayDetails?.gateway_details?.dvl_report_codes);
    setValue('dedicated', gatewayDetails?.is_dedicated);
  }
}, [gatewayDetails]);



  const description = (
    <Box>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction="column"
          gap={2}
          sx={{ p: '25px', overflowY: 'auto', maxHeight: '600px', height: '100%', width: '800px' }}
        >
            <InputContainer>
                <RHFTextField size="large" name="name" label="Name" placeholder="Enter Name." />
            </InputContainer>
            <InputContainer>
              <RHFSelect name="country" label="Select Country" control={control}>
                {Object.entries(allowedCountries).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </RHFSelect>
            </InputContainer>
            <InputContainer>
            <RHFSelect name="route" label="Select Route" control={control}>
              {GATEWAY_ROUTE.map((ele) => (
                <MenuItem key={ele.value} value={ele.value}>
                  {ele.label}
                </MenuItem>
              ))}
            </RHFSelect>
            </InputContainer>
            <InputContainer>
            <RHFSelect name="gatewayType" label="Select Gateway Type" control={control}>
              {GATEWAY_TYPES.map((ele) => (
                <MenuItem key={ele.value} value={ele.value}>
                  {ele.label}
                </MenuItem>
              ))}
            </RHFSelect>
            </InputContainer>
            <InputContainer>
            <RHFTextField name="senderId" label="Sender ID" placeholder="Enter Sender ID." />
            </InputContainer>
            <InputContainer>
            <RHFTextField name="minimumCredit" label="Minimum Credit" placeholder="Enter Minimum Credit." />
            </InputContainer>
            <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
                sx={{ color: 'grey.10' }}>
                Gateway Settings
            </Typography>
            <InputContainer>
            <RHFTextField name="smscHost" label="SMSC Host" placeholder="Enter SMSC Host." />
            </InputContainer>
            <InputContainer>
            <RHFSelect name="smscType" label="Select SMSC Connection type" control={control}>
              {SMSC_TYPE.map((ele) => (
                <MenuItem key={ele.value} value={ele.value}>
                  {ele.label}
                </MenuItem>
              ))}
            </RHFSelect>
            </InputContainer>
            <Box display="flex" justifyContent="space-between" gap = "20px">
            <InputContainer>
                <RHFTextField size="large" name="smscUsername" label="SMSC Username" placeholder="Enter SMSC Username." />
            </InputContainer>
            <InputContainer>
                <RHFTextField size="large" name="smscPassword" label="SMSC Password" placeholder="Enter SMSC Password." />
            </InputContainer>
            </Box>
            <Box display="flex" justifyContent="space-between" gap = "20px">
            <InputContainer>
                <RHFTextField size="large" name="smscSystemType" label="SMSC System Type" placeholder="Enter SMSC System Type." />
            </InputContainer>
            <InputContainer>
                <RHFTextField size="large" name="throughput" label="Throughput" placeholder="Enter Throughput." />
            </InputContainer>
            </Box>
            <Box display="flex" justifyContent="space-between" gap = "20px">
            <InputContainer>
                <RHFTextField size="large" name="smsc_port" label="SMSC TX (Sending) port" placeholder="Enter SMSC System Type." />
            </InputContainer>
            <InputContainer>
                <RHFTextField size="large" name="smsc_receive_port" label="smsc RX (Receive) Port" placeholder="Enter Throughput." />
            </InputContainer>
            </Box>
             <Box display="flex" justifyContent="space-between" gap = "20px">
            <InputContainer>
                <RHFTextField size="large" name="noSessions" label="No. of TX (Sending) Sessions" placeholder="Enter No.of Sessions." />
            </InputContainer>
            <InputContainer>
                <RHFTextField size="large" name="noReceiveSessions" label="No. of RX (Receive) Sessions" placeholder="Enter No. of Receive Sessions." />
            </InputContainer>
            </Box>
            <Box display="flex" justifyContent="space-between" gap="20px">
              <InputContainer sx={{ flex: 1, minWidth: 0 }}>
                <RHFTextField size="large" name="perSessionsTPS" label="Per Session TX (Sending) TPS" placeholder="Enter per sessions  sending per second (TPS)." />
              </InputContainer>
              <Box sx={{ flex: 1 }} />
            </Box>
            <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
                sx={{ color: 'grey.10' }}>
                Delivery Report Settings
            </Typography> 
            <InputContainer>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
              <RHFCheckbox name="all" checked={allSelected} onClick={handleAllToggle} label="All" />
              <RHFMultiCheckbox
                name="dvl_report"
                options={SMSC_DELIVERY_REPORT}
                variant="outlined"
                value={selectedCheckboxes}
                onChange={handleIndividualChange}
              />
            </Box>
            </InputContainer>
            <Typography
              id="modal-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: 'grey.10', mb: 2 }}
            >
              Sending Parameters
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ width: '20%' }} /> {/* Empty space for left-side labels */}
              <Typography sx={{ width: '40%', fontWeight: 600, textAlign: 'center' }}>
                English
              </Typography>
              <Typography sx={{ width: '40%', fontWeight: 600, textAlign: 'center' }}>
                Other
              </Typography>
            </Box>

            {[
              'Coding',
              'Charset',
              'Service',
              'SMS Type',
              'Mclass',
              'Alt dcs',
            ].map((label, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ width: '20%' }}>{label}</Typography>
                <Box sx={{ width: '40%', pr: 2 }}>
                  <RHFTextField
                    size="large"
                    name={`${label.toLowerCase().replace(/ /g, '_')}_english`}
                    placeholder={label}
                  />
                </Box>
                <Box sx={{ width: '40%' }}>
                  <RHFTextField
                    size="large"
                    name={`${label.toLowerCase().replace(/ /g, '_')}_other`}
                    placeholder={label}
                  />
                </Box>
              </Box>
            ))}
            <InputContainer>
            <Typography sx={{ mr : 7 }}>Dedicated</Typography>
            <RHFRadioGroup
              name="dedicated"
              row
              spacing={2}
              container={{ display: 'flex', alignItems: 'center' }}
              options={[
                { value: 1, label: 'Yes' },
                { value: 0, label: 'No' },
              ]}
            />
          </InputContainer>
                      {totalErrors > 0 && (
              <Typography sx={{ color: theme.palette.error.main }}>
                Please fill all required fields!
              </Typography>
            )}

            {checkButtonAccess("smsGatewayPageList", "w") && (       

          <Box sx={{ display: 'flex', gap: '1rem', mt: 3 }}>
            <Box
              sx={{
                width: '7.625rem',
                backgroundColor: canSubmit ? '#212B36' : 'grey',
                borderRadius: '8px',
                p: '0.562rem',
                height: '2.375rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                pointerEvents: canSubmit ? 'auto' : 'none',
              }}
              onClick={handleSubmit(onSubmit)}
            >
              <Typography sx={{ fontWeight: 600, color: '#fff' }}>
                {updateUserId ? "Update" : "Submit"}
                </Typography>
            </Box>
            <Box
              sx={{
                width: '7.625rem',
                backgroundColor: '#fff',
                borderRadius: '8px',
                p: '0.562rem',
                height: '2.375rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              onClick={onCancel}
            >
              <Typography sx={{ fontWeight: 600, color: '#212B36' }}>Cancel</Typography>
            </Box>
          </Box>
            )}

        </Stack>
      </FormProvider>
    </Box>
  );

  return (
    openModal && (
      <CustomModal
        open={openModal}
        title={updateUserId ? "Update SMS Gateway" : "Add New SMS Gateway"}
        description={description}
        close={handleCloseModal}
        isCloseIcon={true}
      />
    )
  );
};

export default GatewaysModal;
